import { useEffect, useId, useRef, useState, type ReactNode } from "react";
import { mountNavigationTransitionGuard } from "./navigationTransition";
import type { DocumentNotificationIdentity } from "./documentNotificationRead";

export function documentDraftChanged(baseline: unknown, current: unknown): boolean {
  return JSON.stringify(baseline) !== JSON.stringify(current);
}

export function useDocumentDraftDirty(open: boolean, draft: unknown): boolean {
  const baselineRef = useRef("");
  const wasOpenRef = useRef(false);
  const serialized = JSON.stringify(draft);

  if (open && !wasOpenRef.current) baselineRef.current = serialized;
  if (!open) baselineRef.current = "";
  wasOpenRef.current = open;

  return open && baselineRef.current !== serialized;
}

export function DocumentWorkspace({
  title,
  subtitle,
  status,
  onClose,
  actions,
  isDirty = false,
  focusTitleOnMount = false,
  restoreFocusOnUnmount = true,
  children,
  notificationIdentity,
}: {
  title: string;
  subtitle?: string;
  status?: ReactNode;
  onClose: () => void;
  actions?: ReactNode;
  isDirty?: boolean;
  focusTitleOnMount?: boolean;
  restoreFocusOnUnmount?: boolean;
  children: ReactNode;
  notificationIdentity?:DocumentNotificationIdentity;
}) {
  useEffect(()=>{
    if(notificationIdentity?.documentId)window.dispatchEvent(new CustomEvent("pharmhub:document-opened",{detail:notificationIdentity}));
  },[notificationIdentity?.module,notificationIdentity?.documentId,notificationIdentity?.version,notificationIdentity?.verificationPath]);
  const titleId = useId();
  const workspaceRef = useRef<HTMLElement>(null);
  const titleRef = useRef<HTMLHeadingElement>(null);
  const continueButtonRef = useRef<HTMLButtonElement>(null);
  const lastWorkspaceFocusRef = useRef<HTMLElement | null>(null);
  const closeRef = useRef(onClose);
  const dirtyRef = useRef(isDirty);
  const closeConfirmationRef = useRef(false);
  const pendingNavigationRef = useRef<null | (() => void)>(null);
  const [closeConfirmation, setCloseConfirmation] = useState(false);

  useEffect(() => { closeRef.current = onClose; }, [onClose]);
  useEffect(() => { dirtyRef.current = isDirty; }, [isDirty]);
  useEffect(() => {
    if (!isDirty) return;
    function handleBeforeUnload(event: BeforeUnloadEvent) {
      event.preventDefault();
      event.returnValue = "";
    }
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [isDirty]);
  useEffect(() => {
    closeConfirmationRef.current = closeConfirmation;
    if (closeConfirmation) continueButtonRef.current?.focus({ preventScroll: true });
  }, [closeConfirmation]);

  function requestClose() {
    pendingNavigationRef.current = null;
    if (isDirty) setCloseConfirmation(true);
    else onClose();
  }

  function continueEditing() {
    pendingNavigationRef.current = null;
    setCloseConfirmation(false);
    window.requestAnimationFrame(() => {
      const focusTarget = lastWorkspaceFocusRef.current;
      if (focusTarget?.isConnected) focusTarget.focus({ preventScroll: true });
      else workspaceRef.current?.focus({ preventScroll: true });
    });
  }

  function discardAndClose() {
    const proceed = pendingNavigationRef.current;
    pendingNavigationRef.current = null;
    setCloseConfirmation(false);
    closeRef.current();
    if (proceed) window.setTimeout(proceed, 0);
  }

  useEffect(() => {
    const previousFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const frame = window.requestAnimationFrame(() => {
      if (focusTitleOnMount) titleRef.current?.focus({ preventScroll: true });
      else workspaceRef.current?.focus({ preventScroll: true });
    });

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") {
        const target = event.target instanceof Element ? event.target : null;
        // Calendars, client dialogs and the notification centre own their
        // Escape key. Closing them must not discard the document below.
        if (!target || !workspaceRef.current?.contains(target) || target.closest('[role="dialog"]')) return;
        const menu = target.closest<HTMLDetailsElement>("details[open]");
        if (menu) {
          event.preventDefault();
          event.stopPropagation();
          menu.open = false;
          menu.querySelector("summary")?.focus();
          return;
        }
        event.preventDefault();
        event.stopPropagation();
        if (closeConfirmationRef.current) continueEditing();
        else if (dirtyRef.current) setCloseConfirmation(true);
        else closeRef.current();
        return;
      }
    }

    document.addEventListener("keydown", handleKeyDown, true);
    const unmountNavigationGuard = mountNavigationTransitionGuard(window, {
      isDirty: () => dirtyRef.current,
      onClose: () => closeRef.current(),
      onDirty: (proceed) => { pendingNavigationRef.current = proceed; setCloseConfirmation(true); },
    });
    return () => {
      window.cancelAnimationFrame(frame);
      document.removeEventListener("keydown", handleKeyDown, true);
      unmountNavigationGuard();
      document.body.style.overflow = previousOverflow;
      if (restoreFocusOnUnmount) {
        window.requestAnimationFrame(() => {
          if (previousFocus?.isConnected) previousFocus.focus({ preventScroll: true });
        });
      }
    };
  }, []);

  return (
    <section
      ref={workspaceRef}
      className="document-workspace"
      role="region"
      aria-labelledby={titleId}
      tabIndex={-1}
      onFocusCapture={(event) => {
        if (!closeConfirmationRef.current && event.target instanceof HTMLElement) {
          lastWorkspaceFocusRef.current = event.target;
        }
      }}
    >
      <header className="document-toolbar">
        <button className="secondary-button document-toolbar__back" type="button" onClick={requestClose}>← К реестру</button>
        <div className="document-title">
          <div className="document-title__copy">
            <h2 ref={titleRef} id={titleId} tabIndex={focusTitleOnMount ? -1 : undefined}>{title}</h2>
            {subtitle && <p>{subtitle}</p>}
          </div>
          {status && <div className="document-title__status">{status}</div>}
        </div>
        {actions && <div className="document-toolbar__actions">{actions}</div>}
      </header>
      {closeConfirmation && <div className="document-close-confirm" role="alert" aria-live="assertive">
        <span><strong>Документ не сохранён</strong>Внесённые данные будут потеряны.</span>
        <div>
          <button ref={continueButtonRef} className="secondary-button" type="button" onClick={continueEditing}>Продолжить редактирование</button>
          <button className="secondary-button document-close-confirm__discard" type="button" onClick={discardAndClose}>Выйти без сохранения</button>
        </div>
      </div>}
      <div className="document-workspace__body">{children}</div>
    </section>
  );
}

export function DocumentSectionHeading({ children }: { children: ReactNode }) {
  return <h3 className="document-section-heading">{children}</h3>;
}

export function DocumentField({ label, children, wide = false }: { label: string; children: ReactNode; wide?: boolean }) {
  return <div className={wide ? "document-field document-field--wide" : "document-field"}><span>{label}</span><strong>{children}</strong></div>;
}
